import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import {
  type AssetType,
  type PartyType,
} from '~/modules/asset-reparsing/types';

import { i18n } from '~/utils';

import { queryKeysAssetReparsing } from '../asset-reparsing/queryKeys';

import { zodAddCompanyIdentifierPayloadSchema } from './validations';

/**
 * Builds a customer-number mapping payload for the old asset-reparsing flow.
 * This flow has a loaded reparsing asset and reads the party issuer-assigned id
 * and issuer owner from `asset.assetParties`.
 *
 * @param {UUID} companyId - The ID of the selected company.
 * @param {PartyType} party - The party from the asset for which the company mapping should be created.
 * @param {AssetType} asset - The asset object containing the asset parties.
 */
const mapBody = (companyId: UUID, party: PartyType, asset: AssetType) => {
  const data = {
    issuerCompanyId: asset.assetParties.issuer.id,
    identifyingText:
      asset.assetParties[party].legalOrganization.issuerAssignedId,
    identifiedCompanyId: companyId,
  };

  return data;
};

/**
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/add_company_identifier_company_identifier_post
 */
// fallow-ignore-next-line unused-export
export const createCompanyIdentifier = async (
  body: ReturnType<typeof mapBody>,
) => {
  try {
    return await vestigasApi.post(ENDPOINT.COMPANY.IDENTIFIER.CREATE(), {
      json: body,
      validate: {
        body: zodAddCompanyIdentifierPayloadSchema,
        response: zodSuccessResponseSchema,
      },
    });
  } catch (error) {
    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 400: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.mapCompanyId.error.400',
          );
          break;
        }

        case 409: {
          error.message = i18n.t(
            'assetReparsing.mutation.mapCompanyId.error.409',
          );
          break;
        }

        case 422: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.mapCompanyId.error.422',
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            'assetReparsing.mutation.mapCompanyId.error.generic',
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * Maps an unmatchable party id from a loaded asset-reparsing asset to the
 * company selected by the user. Use the DNPv2 parsing-case hook when the source
 * data is `dln_data` instead of `asset.assetParties`.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/add_company_identifier_company_identifier_post
 */
export const useMutationMapCompanyId = (
  asset: AssetType,
  options?: UseMutationOptions,
): UseMutationResult => {
  const queryClient = useQueryClient();

  // @ts-expect-error // TODO: figure out how to correctly work with types of useMutation
  return useMutation({
    // @ts-expect-error // TODO: figure out how to correctly work with types of useMutation
    mutationFn({ companyId, party }: { companyId: string; party: PartyType }) {
      const body = mapBody(companyId, party, asset);

      return createCompanyIdentifier(body);
    },
    onError(error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: queryKeysAssetReparsing.getAll(),
      });
    },
    onSuccess() {
      ToastService.info(
        i18n.t(($) => $.assetReparsing.mutation.mapCompanyId.success),
      );
    },
    ...options,
  });
};
